import { FC, useState } from 'react';
import { useParams } from 'react-router-dom';
import styles from './NavigationCodes.module.css';
import { useTranslation } from 'react-i18next';
import { useNavigationCodes } from '@/services/bundles/bundles.service';
import Header from '@/components/Header/Header';
import TableCard from '@/components/Tables/TableCard/TableCard';
import Link from '@/components/Link/Link';
import Button from '@/components/Button/Button';
import { ButtonVariants } from '@/components/Button/Button.types';
import Icon from '@/components/Icons/Icon';
import useConfirmationModal from '@/utils/useConfirmationModal/useConfirmationModal';
import DeleteConfirmationModal from '@/pages/Admin/Bundles/BundleDetails/NavigationCodes/DeleteConfirmationModal/DeleteConfirmationModal';
import { NavigationCode } from '@/types/bundles.types';
import { CellContext } from '@tanstack/react-table';
import ConfirmationModal from '@/components/Modal/ConfirmationModal/ConfirmationModal';

const NavigationCodes: FC = () => {
  const { t } = useTranslation();
  const { bundleId } = useParams();
  const { data, isError, isLoading } = useNavigationCodes(bundleId !== undefined, bundleId!);

  const [deleteConfirmationModalOpen, setDeleteConfirmationModalOpen] = useState(false);
  const [confirmationModalVisible, setConfirmationModalVisible] = useState(false);
  const [rowId, setRowId] = useState('');
  const openDeleteModal = (id: string) => {
    setRowId(id);
    setDeleteConfirmationModalOpen(true);
  };
  const closeDeleteModal = () => setDeleteConfirmationModalOpen(false);
  const { open } = useConfirmationModal();

  // eslint-disable-next-line react/no-unstable-nested-components
  const DeleteNavCode = ({ row }: CellContext<NavigationCode, unknown>) => (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      <Button
        variant={ButtonVariants.Text}
        className={styles.deleteButton}
        onClick={() => openDeleteModal(`${row.original.id}`)}
      >
        <Icon iconName="Trash" />
        {t('freezeCard.deleteButton')}
      </Button>
    </div>
  );

  return (
    <>
      <Header
        title={t('common.bundle.navigationCode')}
        breadcrumb={{ to: `/bundles/${bundleId}`, label: t('breadcrumb.backToBundleDetails') }}
      />
      <div className={styles.page}>
        <div className={styles.tableLabel}>
          <Link to={`/bundles/${bundleId}/addNavCode`} key={bundleId} className={styles.textbuttonDetail}>
            {t('common.bundle.addNavCode')}
          </Link>
        </div>

        <TableCard
          width="calc(100vw - 250px)"
          isLoading={isLoading}
          data={data}
          columns={[
            {
              header: t('common.bundle.navCode'),
              accessorKey: 'navCode',
              enableSorting: true,
            },
            {
              header: t('common.bundle.prices'),
              accessorKey: 'prices',
              enableSorting: true,
            },
            {
              header: 'Action',
              cell: DeleteNavCode,
            },
          ]}
        />
      </div>

      <ConfirmationModal
        title={t('common.bundle.navCode')}
        buttonText={t('freezeCard.backToBundleButton')}
        isOpen={confirmationModalVisible}
        hideStatusUpdateText
        onClose={() => setConfirmationModalVisible(false)}
      />
      <DeleteConfirmationModal
        isOpen={deleteConfirmationModalOpen}
        onClose={closeDeleteModal}
        idToDelete={rowId}
        openConfirmationModal={open}
      />
    </>
  );
};

export default NavigationCodes;
